import { FileArrowUp, UploadSimpleIcon, X } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUploadFilterlist } from "@/api/reseller-manager/mutations";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

interface FilterlistUploadDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	gatewaySn: string;
}

export function FilterlistUploadDialog({
	open,
	onOpenChange,
	gatewaySn,
}: FilterlistUploadDialogProps) {
	const { t } = useTranslation();
	const [file, setFile] = useState<File | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const upload = useUploadFilterlist();

	useEffect(() => {
		if (open) {
			setFile(null);
			setIsDragging(false);
			if (inputRef.current) inputRef.current.value = "";
		}
	}, [open]);

	function handleSubmit() {
		if (!file) return;
		upload.mutate(
			{ mqtt_source_sn: gatewaySn, file },
			{
				onSuccess: () => {
					toast.success(t("filterlistUpload.success"));
					onOpenChange(false);
				},
				onError: () => {
					toast.error(t("filterlistUpload.error"));
				},
			},
		);
	}

	const handleDrop = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const dropped = e.dataTransfer.files[0];
		if (dropped) setFile(dropped);
	}, []);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback(() => setIsDragging(false), []);

	const canSubmit = file && !upload.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("filterlistUpload.title")}</DialogTitle>
					<DialogDescription>
						{t("filterlistUpload.description")}
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label>{t("filterlistUpload.gatewayLabel")}</Label>
						<div className="rounded-md border bg-muted/50 px-3 py-2 text-sm">
							{gatewaySn}
						</div>
					</div>
					<div className="flex flex-col gap-1.5">
						<div className="flex items-center justify-between">
							<Label>{t("filterlistUpload.fileLabel")}</Label>
							<a
								href="/CSV_EXAMPLE_FILTERLIST_IMPORT.csv"
								download
								className="text-xs text-muted-foreground underline hover:text-foreground"
							>
								{t("filterlistUpload.downloadTemplate")}
							</a>
						</div>
						{file ? (
							<div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm">
								<FileArrowUp
									aria-hidden="true"
									className="size-4 shrink-0 text-muted-foreground"
								/>
								<span className="flex-1 truncate">{file.name}</span>
								<button
									type="button"
									aria-label="Remove file"
									onClick={() => {
										setFile(null);
										if (inputRef.current) inputRef.current.value = "";
									}}
									className="shrink-0 text-muted-foreground hover:text-foreground"
								>
									<X aria-hidden="true" className="size-4" />
								</button>
							</div>
						) : (
							<button
								type="button"
								onClick={() => inputRef.current?.click()}
								onDrop={handleDrop}
								onDragOver={handleDragOver}
								onDragLeave={handleDragLeave}
								className={`flex flex-col items-center gap-2 rounded-md border-2 border-dashed px-4 py-8 text-center text-sm transition-colors ${
									isDragging
										? "border-primary bg-primary/5 text-primary"
										: "border-muted-foreground/25 text-muted-foreground hover:border-primary/50 hover:text-foreground"
								}`}
							>
								<UploadSimpleIcon aria-hidden="true" className="size-6" />
								<span>
									{isDragging
										? t("filterlistUpload.dragActive")
										: t("filterlistUpload.dragDrop")}
								</span>
							</button>
						)}
						<input
							ref={inputRef}
							type="file"
							className="hidden"
							onChange={(e) => setFile(e.target.files?.[0] ?? null)}
						/>
					</div>
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={upload.isPending}
					>
						{t("filterlistUpload.cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{upload.isPending
							? t("filterlistUpload.uploading")
							: t("filterlistUpload.upload")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
